import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import SelectAssetDialog from '../tools/selectfile';
import API from '../../API';

export default function UpdateConversationDetailsDialog({
  open,
  onClose,
  conversation,
}) {
  const queryClient = useQueryClient();

  // Mutation to save conversation details directly inside the dialog
  const updateMutation = useMutation(
    details => API.conversations.updateConversationDetails(conversation.id, details),
    {
      onSuccess: updatedConv => {
        // Refresh relevant queries
        queryClient.invalidateQueries(['userSentConversations']);
        queryClient.invalidateQueries(['messages', conversation.id]);

        // Safely update cached list if present
        queryClient.setQueryData(['userSentConversations'], old => {
          if (Array.isArray(old)) {
            return old.map(c => (c.id === conversation.id ? updatedConv : c));
          }
          return old;
        });

        onClose();
      },
      onError: error => {
        console.error('Error updating conversation details:', error);
      },
    }
  );

  const [displayName, setDisplayName] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [themeColor, setThemeColor] = useState('#ffffff');
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);

  useEffect(() => {
    if (conversation) {
      setDisplayName(conversation.display_name || '');
      setCoverImage(conversation.cover_image || '');
      setThemeColor(conversation.theme?.color || '#ffffff');
    }
  }, [conversation, open]);

  const handleSave = () => {
    // Build correct flat payload
    const payload = {
      display_name: displayName.trim(),
      cover_image: coverImage.trim(),
      theme: { color: themeColor }
    };
    updateMutation.mutate(payload);
  };

  const handleAssetClick = file => {
    setCoverImage(file.filename);
    setAssetDialogOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Update Conversation Details</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Display Name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              fullWidth
              disabled={updateMutation.isLoading}
            />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Cover Image URL
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  placeholder="https://example.com/image.jpg"
                  value={coverImage}
                  onChange={e => setCoverImage(e.target.value)}
                  fullWidth
                  disabled={updateMutation.isLoading}
                />
                <Button
                  variant="outlined"
                  onClick={() => setAssetDialogOpen(true)}
                  disabled={updateMutation.isLoading}
                >
                  Select Asset
                </Button>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Theme Color
              </Typography>
              <input
                type="color"
                value={themeColor}
                onChange={e => setThemeColor(e.target.value)}
                style={{ width: '100%', height: 40, border: 'none', padding: 0 }}
                disabled={updateMutation.isLoading}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={updateMutation.isLoading}>
            Cancel
          </Button>

          <Box position="relative">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={updateMutation.isLoading}
            >
              Save
            </Button>
            {updateMutation.isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <SelectAssetDialog
        open={assetDialogOpen}
        onClose={() => setAssetDialogOpen(false)}
        selectedAsset={null}
        onAssetClick={handleAssetClick}
      />
    </>
  );
}
